import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DollarSign,
  Globe,
  TrendingUp,
  Users,
  CreditCard,
  Plus,
  RefreshCw,
  Edit3,
  Trash2,
  Save,
  Eye,
  BarChart3,
  MapPin,
  Check,
  X,
  Calculator,
  ChevronDown,
  ChevronUp,
  Search,
  HeartHandshake
} from "lucide-react";
import CommissionTracker from "./CommissionTracker";

// Default pricing tiers from STRATEGY.md and PROVIDER_SALES_SHEET.md
const DEFAULT_PROVIDER_TIERS = [
  {
    tier_id: "free",
    name: "Free",
    price_monthly: 0,
    price_annual: 0,
    badge_text: "",
    description: "Get started with the basics",
    public_features: [
      "5 client profiles",
      "Basic portfolio",
      "Preference sharing",
      "Client check-in"
    ],
    cta_text: "Get Started",
    cta_url: "/signup",
    is_popular: false,
    tier_type: "provider"
  },
  {
    tier_id: "solo",
    name: "Solo",
    price_monthly: 11,
    price_annual: 110,
    badge_text: "",
    description: "For individual providers ready to grow",
    public_features: [
      "Unlimited client profiles",
      "Full portfolio with tagging",
      "Client preference history",
      "Booking reminders",
      "Analytics dashboard",
      "Personal inspo board"
    ],
    cta_text: "Start Free Trial",
    cta_url: "/signup?tier=solo",
    is_popular: false,
    tier_type: "provider"
  },
  {
    tier_id: "team",
    name: "Team",
    price_monthly: 44,
    price_annual: 440,
    badge_text: "Most Popular",
    description: "For small teams and multi-staff",
    public_features: [
      "Everything in Solo",
      "Up to 5 team members",
      "Shared client directory",
      "Team analytics",
      "Late fee policies"
    ],
    cta_text: "Start Free Trial",
    cta_url: "/signup?tier=team",
    is_popular: true,
    tier_type: "provider"
  },
  {
    tier_id: "luxe",
    name: "Luxe",
    price_monthly: 99,
    price_annual: 990,
    badge_text: "Premium",
    description: "Full-service premium experience",
    public_features: [
      "Everything in Team",
      "NFC reader included ($150 value)",
      "5-10 team seats",
      "Custom branding",
      "Priority support",
      "Account manager"
    ],
    cta_text: "Contact Sales",
    cta_url: "/contact?tier=luxe",
    is_popular: false,
    tier_type: "provider"
  }
];

const DEFAULT_EVENT_TIERS = [
  {
    tier_id: "event_basic",
    name: "Event Basic",
    price_monthly: 0,
    price_annual: 0,
    badge_text: "",
    description: "Free for all event hosts",
    public_features: [
      "Unlimited events",
      "Guest preference collection",
      "RSVP management",
      "Dietary & accessibility tracking"
    ],
    cta_text: "Create Event",
    cta_url: "/host",
    is_popular: false,
    tier_type: "events"
  },
  {
    tier_id: "nfc_reader",
    name: "NFC Reader",
    price_monthly: 150,
    price_annual: 150,
    badge_text: "One-time",
    description: "Physical check-in device",
    public_features: [
      "Tap-to-check-in hardware",
      "Works with any plan",
      "Instant guest recognition"
    ],
    cta_text: "Purchase",
    cta_url: "/shop/nfc",
    is_popular: false,
    tier_type: "events"
  },
  {
    tier_id: "enterprise",
    name: "Enterprise",
    price_monthly: 0,
    price_annual: 0,
    badge_text: "Custom",
    description: "For large venues and multi-event organizers",
    public_features: [
      "Multi-venue support",
      "API access",
      "White-label options",
      "Dedicated support",
      "Custom integrations"
    ],
    cta_text: "Contact Sales",
    cta_url: "/contact?tier=enterprise",
    is_popular: false,
    tier_type: "events"
  }
];

// Milestone projections from STRATEGY.md
const DEFAULT_MILESTONES = [
  { name: "Current pilot", timeline: "Month 0", users: 1, arr: "$0", valuation: "Pre-seed" },
  { name: "Seed traction", timeline: "Month 6", users: 500, arr: "$60-80K", valuation: "$500K-1M" },
  { name: "Series A", timeline: "Month 18", users: 5000, arr: "$600-900K", valuation: "$5-10M" },
  { name: "Acquisition", timeline: "Month 24-36", users: "25-50K", arr: "$3-7M", valuation: "$25-70M" }
];

export default function FinancialsSection() {
  const [activeTab, setActiveTab] = useState("stats");
  const [stats, setStats] = useState({
    total_users: 0,
    free_users: 0,
    monthly_subs: 0,
    yearly_subs: 0,
    mrr: 0,
    arr: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats({
          total_users: data.total_users || 0,
          free_users: data.total_users || 0,
          monthly_subs: 0,
          yearly_subs: 0,
          mrr: 0,
          arr: 0
        });
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="financials-section">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold">Financials</h1>
          <p className="text-gray-500">Revenue, projections, pricing tiers, and revenue share</p>
        </div>
        <Button variant="outline" onClick={fetchStats} data-testid="refresh-financials">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Top-Line Stats - Always Visible */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
        <StatCard label="Total Users" value={stats.total_users} icon={Users} />
        <StatCard label="Free" value={stats.free_users} icon={Users} color="gray" />
        <StatCard label="Monthly Subs" value={stats.monthly_subs} icon={CreditCard} color="blue" />
        <StatCard label="Yearly Subs" value={stats.yearly_subs} icon={CreditCard} color="purple" />
        <StatCard label="MRR (USD)" value={`$${stats.mrr.toFixed(2)}`} icon={DollarSign} color="green" />
        <StatCard label="Annual Rev (USD)" value={`$${stats.arr.toFixed(2)}`} icon={TrendingUp} color="amber" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="stats">Overview</TabsTrigger>
          <TabsTrigger value="projections">Live Projections</TabsTrigger>
          <TabsTrigger value="pricing">Pricing Tiers</TabsTrigger>
          {/* Iter 9.39a - Commission Tracking moved to Product → Portals */}
        </TabsList>

        <TabsContent value="stats" className="mt-6">
          <OverviewTab stats={stats} />
        </TabsContent>

        <TabsContent value="projections" className="mt-6">
          <LiveProjectionsTab />
        </TabsContent>

        <TabsContent value="pricing" className="mt-6">
          <PricingTiersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = "purple" }) {
  const colors = {
    purple: "bg-purple-100 text-purple-600",
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    amber: "bg-amber-100 text-amber-600",
    gray: "bg-gray-100 text-gray-600"
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OverviewTab({ stats }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Revenue Summary</CardTitle>
          <CardDescription>Current financial snapshot (USD)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Monthly Recurring Revenue</span>
              <span className="text-lg font-bold text-green-600">${stats.mrr.toFixed(2)} USD</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Annual Run Rate</span>
              <span className="text-lg font-bold text-purple-600">${stats.arr.toFixed(2)} USD</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Paying Customers</span>
              <span className="text-lg font-bold">{stats.monthly_subs + stats.yearly_subs}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Churn Rate</span>
              <span className="text-lg font-bold text-green-600">0%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Subscription Breakdown</CardTitle>
          <CardDescription>By plan type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-400" />
                <span>Free</span>
              </div>
              <span className="font-bold">{stats.free_users}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span>Solo ($11/mo)</span>
              </div>
              <span className="font-bold">0</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500" />
                <span>Team ($44/mo)</span>
              </div>
              <span className="font-bold">0</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span>Luxe ($99/mo)</span>
              </div>
              <span className="font-bold">0</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>Annual ($110-990/yr)</span>
              </div>
              <span className="font-bold">{stats.yearly_subs}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== LIVE PROJECTIONS TAB ====================
function LiveProjectionsTab() {
  const [projections, setProjections] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCity, setExpandedCity] = useState(null);
  const [showAddCity, setShowAddCity] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [cityResults, setCityResults] = useState([]);
  const [newCity, setNewCity] = useState({ city: "", state: "", country: "", phase: 3 });

  // Fetch projections
  const fetchProjections = useCallback(async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/financial-projections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProjections(data.projections);
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error("Failed to fetch projections:", err);
      toast.error("Failed to load projections");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjections();
  }, [fetchProjections]);

  // Search cities using Places API
  const searchCities = async (query) => {
    if (query.length < 2) {
      setCityResults([]);
      return;
    }
    try {
      const res = await fetch(`${API}/places/autocomplete?input=${encodeURIComponent(query)}&types=(cities)`);
      if (res.ok) {
        const data = await res.json();
        setCityResults(data.predictions || []);
      }
    } catch (err) {
      console.error("City search failed:", err);
    }
  };

  // Update city category target
  const updateCategoryTarget = async (cityId, category, field, value) => {
    if (!projections) return;
    
    const updatedCities = projections.cities.map(city => {
      if (city.city_id === cityId) {
        return {
          ...city,
          categories: {
            ...city.categories,
            [category]: {
              ...city.categories[category],
              [field]: parseInt(value) || 0
            }
          }
        };
      }
      return city;
    });

    setProjections({ ...projections, cities: updatedCities });
  };

  // Save city projection
  const saveCityProjection = async (city) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/financial-projections/city/${city.city_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ categories: city.categories, status: city.status })
      });
      if (res.ok) {
        toast.success(`Saved projections for ${city.city}`);
        fetchProjections(); // Refresh to get calculated values
      } else {
        toast.error("Failed to save");
      }
    } catch (err) {
      toast.error("Error saving");
    } finally {
      setSaving(false);
    }
  };

  // Add new city
  const handleAddCity = async (cityData) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/financial-projections/city`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(cityData)
      });
      if (res.ok) {
        toast.success(`Added ${cityData.city}`);
        setShowAddCity(false);
        setNewCity({ city: "", state: "", country: "", phase: 3 });
        fetchProjections();
      }
    } catch (err) {
      toast.error("Failed to add city");
    }
  };

  // Delete city
  const handleDeleteCity = async (cityId, cityName) => {
    if (!confirm(`Delete ${cityName} from projections?`)) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/financial-projections/city/${cityId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("City removed");
        fetchProjections();
      }
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Save assumptions
  const saveAssumptions = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/financial-projections/assumptions`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          assumptions: projections.assumptions,
          pricing: projections.pricing
        })
      });
      if (res.ok) {
        toast.success("Assumptions saved");
        fetchProjections();
      }
    } catch (err) {
      toast.error("Failed to save assumptions");
    }
  };

  // Calculate city total
  const getCityTotal = (city, field) => {
    return Object.values(city.categories || {}).reduce((sum, cat) => sum + (cat[field] || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const calculated = projections?.calculated || {};

  const fmt = (n) => {
    if (!n || n === 0) return "0";
    if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
    if (n >= 1000) return Math.round(n / 1000).toLocaleString() + "K";
    return n.toLocaleString();
  };

  // Filter out "Events" category since there's no revenue there yet
  const filteredCategories = categories.filter(cat => cat.toLowerCase() !== 'events');

  return (
    <div className="space-y-6" data-testid="live-projections-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-blue-700 truncate">{fmt(calculated.total_market_size)}</p>
            <p className="text-xs text-blue-600">Market Size</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-purple-700 truncate">{fmt(calculated.total_target)}</p>
            <p className="text-xs text-purple-600">Target Providers</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-green-700 truncate">{fmt(calculated.paying_providers)}</p>
            <p className="text-xs text-green-600">Est. Paying</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-amber-700 truncate">${fmt(calculated.projected_mrr)}</p>
            <p className="text-xs text-amber-600">Projected MRR</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-3 text-center">
            <p className="text-xl sm:text-2xl font-bold text-emerald-700 truncate">${fmt(calculated.projected_arr)}</p>
            <p className="text-xs text-emerald-600">Projected ARR</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowAddCity(true)} data-testid="add-city-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add City
          </Button>
          <Button variant="outline" onClick={() => setShowAssumptions(true)} data-testid="edit-assumptions-btn">
            <Calculator className="w-4 h-4 mr-2" />
            Edit Assumptions
          </Button>
        </div>
        <Button variant="outline" onClick={fetchProjections}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* City Projections */}
      <Card>
        <CardHeader>
          <CardTitle>City Provider Targets by Category</CardTitle>
          <CardDescription>
            Edit target adoption goals and market size (total providers in each city/category from your research). Changes auto-calculate revenue projections.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Phase Headers */}
            {[1, 2, 3].map(phase => {
              const phaseCities = (projections?.cities || []).filter(c => c.phase === phase);
              if (phaseCities.length === 0) return null;
              
              const phaseLabels = {
                1: "Phase 1 (Months 0-6)",
                2: "Phase 2 (Months 6-18)",
                3: "Phase 3 (Months 18-36)"
              };
              
              const phaseColors = {
                1: "bg-purple-100 border-purple-300 text-purple-800",
                2: "bg-blue-100 border-blue-300 text-blue-800",
                3: "bg-gray-100 border-gray-300 text-gray-800"
              };
              
              return (
                <div key={phase} className="space-y-3">
                  <div className={`px-4 py-2 rounded-lg border ${phaseColors[phase]}`}>
                    <h3 className="font-semibold">{phaseLabels[phase]}</h3>
                  </div>
                  
                  {phaseCities.map(city => (
                    <CityProjectionRow
                      key={city.city_id}
                      city={city}
                      categories={filteredCategories}
                      expanded={expandedCity === city.city_id}
                      onToggle={() => setExpandedCity(expandedCity === city.city_id ? null : city.city_id)}
                      onUpdateCategory={(cat, field, val) => updateCategoryTarget(city.city_id, cat, field, val)}
                      onSave={() => saveCityProjection(city)}
                      onDelete={() => handleDeleteCity(city.city_id, city.city)}
                      onChangePhase={async (newPhase) => {
                        const token = localStorage.getItem("session_token");
                        try {
                          await fetch(`${API}/admin/financial-projections/city/${city.city_id}`, {
                            method: "PUT",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({ phase: newPhase })
                          });
                          toast.success(`Moved ${city.city} to Phase ${newPhase}`);
                          fetchProjections();
                        } catch (err) {
                          toast.error("Failed to change phase");
                        }
                      }}
                      saving={saving}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Add City Dialog */}
      <Dialog open={showAddCity} onOpenChange={setShowAddCity}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add City to Projections</DialogTitle>
            <DialogDescription>
              Search for a city to add to your 2026 roadmap projections
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Search City</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={citySearch}
                  onChange={(e) => {
                    setCitySearch(e.target.value);
                    searchCities(e.target.value);
                  }}
                  placeholder="Start typing a city name..."
                  className="pl-10"
                  data-testid="city-search-input"
                />
              </div>
              
              {/* City Results */}
              {cityResults.length > 0 && (
                <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                  {cityResults.map((result, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        // Parse the description to get city, state, country
                        const parts = result.description.split(", ");
                        setNewCity({
                          city: parts[0] || "",
                          state: parts.length > 2 ? parts[1] : "",
                          country: parts[parts.length - 1] || "",
                          phase: 3
                        });
                        setCitySearch(result.description);
                        setCityResults([]);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-2"
                    >
                      <MapPin className="w-4 h-4 text-gray-400" />
                      {result.description}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Manual Entry */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={newCity.city}
                  onChange={(e) => setNewCity({ ...newCity, city: e.target.value })}
                  placeholder="City name"
                />
              </div>
              <div>
                <Label>State/Region</Label>
                <Input
                  value={newCity.state}
                  onChange={(e) => setNewCity({ ...newCity, state: e.target.value })}
                  placeholder="CA, NY, etc."
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={newCity.country}
                  onChange={(e) => setNewCity({ ...newCity, country: e.target.value })}
                  placeholder="USA, UK, etc."
                />
              </div>
              <div>
                <Label>Phase</Label>
                <Select value={String(newCity.phase)} onValueChange={(v) => setNewCity({ ...newCity, phase: parseInt(v) })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Phase 1 (0-6 months)</SelectItem>
                    <SelectItem value="2">Phase 2 (6-18 months)</SelectItem>
                    <SelectItem value="3">Phase 3 (18-36 months)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCity(false)}>Cancel</Button>
            <Button onClick={() => handleAddCity(newCity)} disabled={!newCity.city}>
              <Plus className="w-4 h-4 mr-2" />
              Add City
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assumptions Dialog */}
      <Dialog open={showAssumptions} onOpenChange={setShowAssumptions}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Revenue Projection Assumptions</DialogTitle>
            <DialogDescription>
              These percentages determine how targets convert to projected revenue
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div>
              <h4 className="font-medium mb-3">Conversion Rates</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Free Users %</Label>
                  <Input
                    type="number"
                    value={projections?.assumptions?.free_percent || 70}
                    onChange={(e) => setProjections({
                      ...projections,
                      assumptions: { ...projections.assumptions, free_percent: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div>
                  <Label>Solo Plan %</Label>
                  <Input
                    type="number"
                    value={projections?.assumptions?.solo_percent || 20}
                    onChange={(e) => setProjections({
                      ...projections,
                      assumptions: { ...projections.assumptions, solo_percent: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div>
                  <Label>Team Plan %</Label>
                  <Input
                    type="number"
                    value={projections?.assumptions?.team_percent || 8}
                    onChange={(e) => setProjections({
                      ...projections,
                      assumptions: { ...projections.assumptions, team_percent: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div>
                  <Label>Luxe Plan %</Label>
                  <Input
                    type="number"
                    value={projections?.assumptions?.luxe_percent || 2}
                    onChange={(e) => setProjections({
                      ...projections,
                      assumptions: { ...projections.assumptions, luxe_percent: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Annual Payment %</Label>
                  <Input
                    type="number"
                    value={projections?.assumptions?.annual_percent || 30}
                    onChange={(e) => setProjections({
                      ...projections,
                      assumptions: { ...projections.assumptions, annual_percent: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Pricing</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Solo Monthly</Label>
                  <Input
                    type="number"
                    value={projections?.pricing?.solo_monthly || 11}
                    onChange={(e) => setProjections({
                      ...projections,
                      pricing: { ...projections.pricing, solo_monthly: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div>
                  <Label>Team Monthly</Label>
                  <Input
                    type="number"
                    value={projections?.pricing?.team_monthly || 44}
                    onChange={(e) => setProjections({
                      ...projections,
                      pricing: { ...projections.pricing, team_monthly: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div>
                  <Label>Luxe Monthly</Label>
                  <Input
                    type="number"
                    value={projections?.pricing?.luxe_monthly || 99}
                    onChange={(e) => setProjections({
                      ...projections,
                      pricing: { ...projections.pricing, luxe_monthly: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssumptions(false)}>Cancel</Button>
            <Button onClick={() => { saveAssumptions(); setShowAssumptions(false); }}>
              <Save className="w-4 h-4 mr-2" />
              Save Assumptions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// City Projection Row Component
function CityProjectionRow({ city, categories, expanded, onToggle, onUpdateCategory, onSave, onDelete, onChangePhase, saving }) {
  const totalTarget = Object.values(city.categories || {}).reduce((sum, c) => sum + (c.target || 0), 0);
  const totalMarketSize = Object.values(city.categories || {}).reduce((sum, c) => sum + (c.market_size || 0), 0);
  
  const statusColors = {
    active: "bg-green-100 text-green-700",
    planned: "bg-gray-100 text-gray-700",
    completed: "bg-blue-100 text-blue-700"
  };
  
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`city-row-${city.city_id}`}>
      {/* City Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-purple-500" />
          <div className="text-left">
            <p className="font-medium">
              {city.city}{city.state && `, ${city.state}`}
            </p>
            <p className="text-sm text-gray-500">{city.country}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Badge className={statusColors[city.status]}>{city.status}</Badge>
          <div className="text-right">
            <p className="font-bold text-lg text-blue-600">{totalMarketSize.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Market Size</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-lg">{totalTarget.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Target</p>
          </div>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </button>
      
      {/* Expanded Category Editor */}
      {expanded && (
        <div className="border-t bg-gray-50 p-4">
          {/* Phase Selector */}
          <div className="flex items-center gap-4 mb-4 pb-3 border-b">
            <Label className="text-sm font-medium text-gray-600">Move to Phase:</Label>
            <div className="flex gap-2">
              {[1, 2, 3].map(phase => (
                <Button
                  key={phase}
                  variant={city.phase === phase ? "default" : "outline"}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (city.phase !== phase) onChangePhase(phase);
                  }}
                  className={city.phase === phase ? "bg-purple-600" : ""}
                  data-testid={`phase-${phase}-btn-${city.city_id}`}
                >
                  Phase {phase}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
            {categories.map(cat => {
              const catKey = cat.toLowerCase();
              const catData = city.categories?.[catKey] || { target: 0, market_size: 0 };
              
              return (
                <div key={cat} className="space-y-2">
                  <Label className="text-xs font-medium text-gray-600">{cat}</Label>
                  <div className="space-y-1">
                    <Input
                      type="number"
                      value={catData.market_size || 0}
                      onChange={(e) => onUpdateCategory(catKey, "market_size", e.target.value)}
                      placeholder="Market Size"
                      className="h-8 text-sm bg-blue-50"
                      title="Total providers in this city/category (from research)"
                      data-testid={`${city.city_id}-${catKey}-market-size`}
                    />
                    <Input
                      type="number"
                      value={catData.target || 0}
                      onChange={(e) => onUpdateCategory(catKey, "target", e.target.value)}
                      placeholder="Target"
                      className="h-8 text-sm"
                      title="Your adoption goal"
                      data-testid={`${city.city_id}-${catKey}-target`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex items-center justify-between pt-3 border-t">
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700">
              <Trash2 className="w-4 h-4 mr-1" />
              Remove City
            </Button>
            <Button onClick={onSave} disabled={saving} size="sm">
              {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PricingTiersTab() {
  const [providerTiers, setProviderTiers] = useState(DEFAULT_PROVIDER_TIERS);
  const [eventTiers, setEventTiers] = useState(DEFAULT_EVENT_TIERS);
  const [productFeatures, setProductFeatures] = useState([]);
  const [editingTier, setEditingTier] = useState(null);
  const [activeType, setActiveType] = useState("provider");
  const [activeView, setActiveView] = useState("tiers"); // tiers or features
  const [loading, setLoading] = useState(false);

  const currentTiers = activeType === "provider" ? providerTiers : eventTiers;
  const setCurrentTiers = activeType === "provider" ? setProviderTiers : setEventTiers;

  // Fetch product features
  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/product-features`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProductFeatures(data.features || []);
        }
      } catch (err) {
        console.error("Failed to fetch features:", err);
      }
    };
    fetchFeatures();
  }, []);

  // Link features to tier
  const linkFeaturesToTier = async (tierId, features, isPublic = true) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/pricing-tiers/${tierId}/link-features`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          [isPublic ? "public_features" : "internal_features"]: features
        })
      });
      if (res.ok) {
        toast.success("Features linked to tier");
        // Update local state
        const updated = currentTiers.map(t => 
          t.tier_id === tierId 
            ? { ...t, [isPublic ? "public_features" : "internal_features"]: features }
            : t
        );
        setCurrentTiers(updated);
      }
    } catch (err) {
      toast.error("Failed to link features");
    } finally {
      setLoading(false);
    }
  };

  const saveTier = async (tier) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/pricing-tiers/${tier.tier_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(tier)
      });
      if (res.ok) {
        toast.success("Tier saved and synced to pricing page");
        setEditingTier(null);
      }
    } catch (err) {
      // Save locally anyway for now
      const updated = currentTiers.map(t => t.tier_id === tier.tier_id ? tier : t);
      setCurrentTiers(updated);
      toast.success("Tier updated locally");
      setEditingTier(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* View Selector */}
      <div className="flex flex-wrap gap-2 justify-between">
        <div className="flex gap-2">
          <Button 
            variant={activeType === "provider" ? "default" : "outline"}
            onClick={() => setActiveType("provider")}
          >
            Provider Plans
          </Button>
          <Button 
            variant={activeType === "events" ? "default" : "outline"}
            onClick={() => setActiveType("events")}
          >
            Events & Enterprise
          </Button>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={activeView === "tiers" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setActiveView("tiers")}
          >
            <CreditCard className="w-4 h-4 mr-1" />
            Tiers
          </Button>
          <Button 
            variant={activeView === "features" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setActiveView("features")}
          >
            <Check className="w-4 h-4 mr-1" />
            Feature Matrix
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="py-4">
          <p className="text-sm text-purple-800">
            <strong>Pricing Sync:</strong> Changes here propagate to /pricing within 1 second. 
            Public features are shown to customers. Internal features are admin-only for access control.
          </p>
        </CardContent>
      </Card>

      {/* Tier Cards View */}
      {activeView === "tiers" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {currentTiers.map((tier) => (
            <TierCard 
              key={tier.tier_id} 
              tier={tier} 
              onEdit={() => setEditingTier(tier)}
              onSave={saveTier}
            />
          ))}
        </div>
      )}

      {/* Feature Matrix View */}
      {activeView === "features" && (
        <Card>
          <CardHeader>
            <CardTitle>Feature-Tier Matrix</CardTitle>
            <CardDescription>
              Check which features are included in each tier. Changes sync to the public pricing page.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-700 min-w-[200px]">Feature</th>
                    {currentTiers.map(tier => (
                      <th key={tier.tier_id} className="text-center p-3 font-medium text-gray-700 min-w-[100px]">
                        <div>{tier.name}</div>
                        {tier.price_monthly > 0 && (
                          <div className="text-xs text-gray-500 font-normal">${tier.price_monthly}/mo</div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Gather all unique features */}
                  {(() => {
                    const allFeatures = new Set();
                    currentTiers.forEach(tier => {
                      (tier.public_features || []).forEach(f => allFeatures.add(f));
                    });
                    return Array.from(allFeatures).map((feature, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm">{feature}</td>
                        {currentTiers.map(tier => {
                          const hasFeature = (tier.public_features || []).includes(feature);
                          return (
                            <td key={tier.tier_id} className="text-center p-3">
                              <button
                                onClick={() => {
                                  const newFeatures = hasFeature
                                    ? (tier.public_features || []).filter(f => f !== feature)
                                    : [...(tier.public_features || []), feature];
                                  linkFeaturesToTier(tier.tier_id, newFeatures, true);
                                }}
                                disabled={loading}
                                className={`w-6 h-6 rounded-full transition-colors ${
                                  hasFeature 
                                    ? "bg-green-500 text-white" 
                                    : "bg-gray-200 text-gray-400 hover:bg-gray-300"
                                }`}
                              >
                                {hasFeature ? (
                                  <Check className="w-4 h-4 mx-auto" />
                                ) : (
                                  <X className="w-4 h-4 mx-auto" />
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            {/* Add New Feature */}
            <div className="mt-6 pt-6 border-t">
              <FeatureAdder 
                onAdd={(feature) => {
                  // Add to the first paid tier by default
                  const firstPaidTier = currentTiers.find(t => t.price_monthly > 0);
                  if (firstPaidTier) {
                    const newFeatures = [...(firstPaidTier.public_features || []), feature];
                    linkFeaturesToTier(firstPaidTier.tier_id, newFeatures, true);
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      {editingTier && (
        <TierEditDialog
          tier={editingTier}
          open={!!editingTier}
          onClose={() => setEditingTier(null)}
          onSave={saveTier}
        />
      )}

      {/* Subscription Tier Visibility */}
      <Card className="mt-4">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-3" data-testid="tier-visibility-heading">Subscription Tier Visibility</h3>
          <p className="text-xs text-gray-500 mb-3">Control which pricing tiers are visible to users on the public pricing page</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {currentTiers.map(tier => {
              const isVisible = tier.is_visible !== false;
              return (
                <div key={tier.tier_id} className={`p-3 rounded-xl border ${isVisible ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"}`} data-testid={`tier-vis-${tier.tier_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{tier.name}</span>
                    <Switch checked={isVisible} onCheckedChange={async (checked) => {
                      const updated = currentTiers.map(t => t.tier_id === tier.tier_id ? { ...t, is_visible: checked } : t);
                      setCurrentTiers(updated);
                      await saveTier({ ...tier, is_visible: checked });
                    }} data-testid={`toggle-vis-${tier.tier_id}`} />
                  </div>
                  <div className="text-xs text-gray-500">${tier.price_monthly || 0}/mo</div>
                  <Badge variant={isVisible ? "default" : "secondary"} className="text-[10px] mt-1">{isVisible ? "Visible" : "Hidden"}</Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FeatureAdder({ onAdd }) {
  const [newFeature, setNewFeature] = useState("");
  
  const handleAdd = () => {
    if (newFeature.trim()) {
      onAdd(newFeature.trim());
      setNewFeature("");
    }
  };
  
  return (
    <div className="flex gap-2">
      <Input
        placeholder="Add new feature..."
        value={newFeature}
        onChange={(e) => setNewFeature(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        className="max-w-xs"
      />
      <Button onClick={handleAdd} disabled={!newFeature.trim()}>
        <Plus className="w-4 h-4 mr-1" />
        Add Feature
      </Button>
    </div>
  );
}

function TierCard({ tier, onEdit }) {
  return (
    <Card className={tier.is_popular ? "border-purple-500 border-2" : ""}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{tier.name}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Edit3 className="w-4 h-4" />
          </Button>
        </div>
        {tier.badge_text && (
          <Badge className="w-fit bg-purple-500">{tier.badge_text}</Badge>
        )}
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          {tier.price_monthly === 0 && tier.tier_id !== "nfc_reader" ? (
            <p className="text-3xl font-bold">Free</p>
          ) : tier.tier_id === "enterprise" ? (
            <p className="text-3xl font-bold">Custom</p>
          ) : (
            <>
              <p className="text-3xl font-bold">${tier.price_monthly} <span className="text-sm font-normal text-gray-500">USD/mo</span></p>
              {tier.price_annual > 0 && tier.price_annual !== tier.price_monthly && (
                <p className="text-sm text-gray-500">${tier.price_annual} USD/year (save 2 months)</p>
              )}
            </>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
        <div className="space-y-2">
          {tier.public_features?.slice(0, 4).map((feature, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
          {tier.public_features?.length > 4 && (
            <p className="text-xs text-gray-400">+{tier.public_features.length - 4} more features</p>
          )}
        </div>
        <Button className="w-full mt-4" variant={tier.is_popular ? "default" : "outline"}>
          {tier.cta_text}
        </Button>
      </CardContent>
    </Card>
  );
}

function TierEditDialog({ tier, open, onClose, onSave }) {
  const [editedTier, setEditedTier] = useState({ ...tier });
  const [featuresText, setFeaturesText] = useState(tier.public_features?.join("\n") || "");

  const handleSave = () => {
    const updated = {
      ...editedTier,
      public_features: featuresText.split("\n").filter(f => f.trim())
    };
    onSave(updated);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Tier: {tier.name}</DialogTitle>
          <DialogDescription>
            Changes sync to /pricing page automatically
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tier Name</Label>
              <Input
                value={editedTier.name}
                onChange={(e) => setEditedTier({ ...editedTier, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Badge Text (leave empty for none)</Label>
              <Input
                value={editedTier.badge_text}
                onChange={(e) => setEditedTier({ ...editedTier, badge_text: e.target.value })}
                placeholder="e.g., Most Popular"
              />
            </div>
            <div>
              <Label>Monthly Price ($)</Label>
              <Input
                type="number"
                value={editedTier.price_monthly}
                onChange={(e) => setEditedTier({ ...editedTier, price_monthly: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Annual Price ($)</Label>
              <Input
                type="number"
                value={editedTier.price_annual}
                onChange={(e) => setEditedTier({ ...editedTier, price_annual: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Input
              value={editedTier.description}
              onChange={(e) => setEditedTier({ ...editedTier, description: e.target.value })}
            />
          </div>

          <div>
            <Label>Public Features (one per line - shown on /pricing)</Label>
            <Textarea
              value={featuresText}
              onChange={(e) => setFeaturesText(e.target.value)}
              rows={6}
              placeholder="Unlimited clients&#10;Full portfolio&#10;Analytics dashboard"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>CTA Button Text</Label>
              <Input
                value={editedTier.cta_text}
                onChange={(e) => setEditedTier({ ...editedTier, cta_text: e.target.value })}
              />
            </div>
            <div>
              <Label>CTA URL</Label>
              <Input
                value={editedTier.cta_url}
                onChange={(e) => setEditedTier({ ...editedTier, cta_url: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={editedTier.is_popular}
              onCheckedChange={(checked) => setEditedTier({ ...editedTier, is_popular: checked })}
            />
            <Label>Mark as "Most Popular"</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />
            Save & Sync
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevenueShareTab() {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newShare, setNewShare] = useState({
    partner_name: "",
    partner_type: "investor",
    percentage: 0,
    agreement_type: "",
    notes: ""
  });

  useEffect(() => {
    fetchShares();
  }, []);

  const fetchShares = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/revenue-shares`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setShares(data.shares || []);
      }
    } catch (err) {
      console.error("Failed to fetch shares:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddShare = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/revenue-shares`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newShare)
      });
      if (res.ok) {
        toast.success("Revenue share added!");
        setShowAddDialog(false);
        setNewShare({ partner_name: "", partner_type: "investor", percentage: 0, agreement_type: "", notes: "" });
        fetchShares();
      } else {
        toast.error("Failed to add share");
      }
    } catch (err) {
      toast.error("Error adding share");
    }
  };

  const partnerTypes = [
    { id: "investor", label: "Investor", color: "bg-amber-500" },
    { id: "pilot_partner", label: "Pilot Partner", color: "bg-purple-500" },
    { id: "sales_partner", label: "Sales Partner", color: "bg-blue-500" },
    { id: "affiliate", label: "Affiliate", color: "bg-green-500" }
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Revenue Share Agreements</CardTitle>
              <CardDescription>Track revenue splits with investors, partners, and affiliates</CardDescription>
            </div>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Agreement
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {shares.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">No revenue share agreements</p>
              <p className="text-sm text-gray-500 mt-1">Add investors or partners to track their revenue splits</p>
            </div>
          ) : (
            <div className="space-y-3">
              {shares.map((share, idx) => (
                <div key={share.share_id || idx} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full ${partnerTypes.find(t => t.id === share.partner_type)?.color || 'bg-gray-500'} flex items-center justify-center`}>
                      <span className="text-white font-bold text-sm">
                        {share.partner_name?.charAt(0) || "?"}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{share.partner_name}</p>
                      <p className="text-sm text-gray-500">
                        {partnerTypes.find(t => t.id === share.partner_type)?.label || share.partner_type}
                        {share.agreement_type && ` • ${share.agreement_type}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-600">{share.percentage}%</p>
                    <p className="text-xs text-gray-500">Revenue Share</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Revenue Share Agreement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Partner/Investor Name</Label>
              <Input
                value={newShare.partner_name}
                onChange={(e) => setNewShare({ ...newShare, partner_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newShare.partner_type} onValueChange={(v) => setNewShare({ ...newShare, partner_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {partnerTypes.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Revenue Share Percentage</Label>
              <Input
                type="number"
                value={newShare.percentage}
                onChange={(e) => setNewShare({ ...newShare, percentage: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Agreement Type (optional)</Label>
              <Input
                value={newShare.agreement_type}
                onChange={(e) => setNewShare({ ...newShare, agreement_type: e.target.value })}
                placeholder="e.g., SAFE, Revenue Share, etc."
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newShare.notes}
                onChange={(e) => setNewShare({ ...newShare, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddShare}>Add Agreement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
